/**
 * SecurityCountdown
 *
 * Re-rendering ticker that formats time-until-lockAt as either
 * "{days}d {hours}h" (>1 day remaining) or "{hours}h {minutes}m" (<1 day).
 * Ticks every 60 s to keep render cost trivial.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';

const TICK_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

const SecurityCountdown = ({ lockAt, className }) => {
  const { t } = useI18n();
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  if (!lockAt) return null;
  const lockMs = Date.parse(lockAt);
  if (!Number.isFinite(lockMs)) return null;

  const remaining = lockMs - Date.now();
  if (remaining <= 0) return null;

  let text;
  if (remaining > DAY_MS) {
    const days = Math.floor(remaining / DAY_MS);
    const hours = Math.floor((remaining % DAY_MS) / HOUR_MS);
    text = t(
      'SecurityUpdate.modal.countdownDaysHours',
      { days, hours },
      `Locks in ${days}d ${hours}h`,
    );
  } else if (remaining > MINUTE_MS) {
    const hours = Math.floor(remaining / HOUR_MS);
    const minutes = Math.floor((remaining % HOUR_MS) / MINUTE_MS);
    text = t(
      'SecurityUpdate.modal.countdownHoursMinutes',
      { hours, minutes },
      `Locks in ${hours}h ${minutes}m`,
    );
  } else {
    text = t(
      'SecurityUpdate.modal.countdownImminent',
      {},
      'Locks in less than a minute',
    );
  }

  return <span className={className}>{text}</span>;
};

SecurityCountdown.propTypes = {
  lockAt: PropTypes.string,
  className: PropTypes.string,
};

export default SecurityCountdown;
